/**
 * components/pdf/PdfEditLayer – Zeichen-Ebene des PDF-Editors (Welle 91, als eigenes Bauteil seit Welle 136).
 * Genutzt von der Verschieben-Seite (Dokument bearbeiten) und vom PDF-Bearbeiter der KRV-Seite.
 */
import React, { useRef, useState } from "react";
import {
  FaMousePointer, FaFont, FaCheck, FaTimes, FaStamp, FaPen, FaRegSquare, FaHighlighter, FaUnderline, FaEraser,
} from "react-icons/fa";
import "./PdfEditLayer.css";

/* == PDF-Editor-Layer (Welle 91) ==========================================
   Liegt absolut über dem PDF-Canvas. Alle Annotationen leben in PDF-User-
   Space-Punkten (Ursprung unten links) — dadurch zoom-invariant; die
   Umrechnung passiert nur beim Rendern über den pdf.js-Viewport. */
export const EDIT_TOOLS = [
  { key: "auswahl",  Icon: FaMousePointer, title: "Auswählen/Verschieben" },
  { key: "text",     Icon: FaFont,         title: "Text hinzufügen" },
  { key: "haken",    Icon: FaCheck,        title: "Haken stempeln" },
  { key: "kreuz",    Icon: FaTimes,        title: "Kreuz stempeln" },
  { key: "stempel",  Icon: FaStamp,        title: "Stempel „Überwiesen am …“ (Text änderbar)" },
  { key: "stift",    Icon: FaPen,          title: "Frei zeichnen" },
  { key: "kasten",   Icon: FaRegSquare,    title: "Kasten" },
  { key: "marker",   Icon: FaHighlighter,  title: "Gelb markieren" },
  { key: "linie",    Icon: FaUnderline,    title: "Unterstreichen" },
  { key: "abdecken", Icon: FaEraser,       title: "Abdecken (weiß) — alten Text ersetzen" },
];
export const EDIT_FARBEN = ["#000000", "#c62828", "#1565c0", "#2e7d32"];
const _TOOL_ZU_TYP = { stift: "pfad", kasten: "rechteck", marker: "marker", linie: "linie", abdecken: "abdecken" };

export default function PdfEditLayer({ vp, seite, tool, annos, setAnnos, felder, setFelder, groesse, farbe, aktiveId, setAktiveId }) {
  const layerRef = useRef(null);
  const [draft, setDraft] = useState(null);       // Vorschau während des Ziehens (PDF-Punkte)
  const dragRef = useRef(null);                   // { modus:"zeichnen"|"move", ... }
  const neuBoxRef = useRef(null);                 // gerade angelegte Textbox (bekommt den Fokus)
  const textRefs = useRef({});                    // id -> textarea, für gezieltes Fokussieren

  const scale = vp.scale || Math.abs(vp.transform?.[0]) || 1;
  const toPdf = (e) => {
    const r = layerRef.current.getBoundingClientRect();
    return vp.convertToPdfPoint(e.clientX - r.left, e.clientY - r.top);
  };
  const toCss = (x, y) => vp.convertToViewportPoint(x, y);

  const neuId = () => Date.now() + Math.random();

  const onPointerDown = (e) => {
    if (e.target.closest(".v-edit-feld") || e.target.closest(".v-edit-textbox")) return;
    const p = toPdf(e);
    if (tool === "text") {
      // preventDefault: sonst holt sich das nachfolgende mousedown den Fokus zurück,
      // die frische Box blurrt sofort und löscht sich selbst (leerer Text).
      e.preventDefault();
      const id = neuId();
      neuBoxRef.current = id;
      setAnnos((prev) => [...prev, { id, typ: "text", seite, x: p[0], y: p[1], text: "", groesse, farbe }]);
      setAktiveId(id);
    } else if (tool === "stempel") {
      // Welle 135: Text mit Rahmen, vorbelegt „Überwiesen am <heute>“ – bleibt editierbar wie jeder Text
      e.preventDefault();
      const id = neuId();
      neuBoxRef.current = id;
      const heute = new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
      setAnnos((prev) => [...prev, { id, typ: "text", rahmen: true, seite, x: p[0], y: p[1],
        text: `Überwiesen am ${heute}`, groesse, farbe }]);
      setAktiveId(id);
    } else if (tool === "haken" || tool === "kreuz") {
      setAnnos((prev) => [...prev, { id: neuId(), typ: tool === "haken" ? "stempel_haken" : "stempel_kreuz",
        seite, x: p[0], y: p[1], groesse: groesse * 1.3, farbe, breite: 2 }]);
    } else if (_TOOL_ZU_TYP[tool]) {
      dragRef.current = { modus: "zeichnen" };
      layerRef.current.setPointerCapture(e.pointerId);
      setDraft({ typ: _TOOL_ZU_TYP[tool], punkte: [p, p], farbe });
    } else if (tool === "auswahl") {
      setAktiveId(null);
    }
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const p = toPdf(e);
    if (d.modus === "zeichnen") {
      setDraft((prev) => {
        if (!prev) return prev;
        if (prev.typ === "pfad") return { ...prev, punkte: [...prev.punkte, p] };
        return { ...prev, punkte: [prev.punkte[0], p] };
      });
    } else if (d.modus === "move") {
      const dx = p[0] - d.start[0], dy = p[1] - d.start[1];
      setAnnos((prev) => prev.map((a) => {
        if (a.id !== d.id) return a;
        if (a.punkte?.length) return { ...a, punkte: d.orig.punkte.map(([x, y]) => [x + dx, y + dy]) };
        return { ...a, x: d.orig.x + dx, y: d.orig.y + dy };
      }));
    }
  };
  const onPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d?.modus === "zeichnen" && draft) {
      const [p1, p2] = [draft.punkte[0], draft.punkte[draft.punkte.length - 1]];
      const winzig = Math.abs(p1[0] - p2[0]) < 2 && Math.abs(p1[1] - p2[1]) < 2 && draft.typ !== "pfad";
      if (!winzig && !(draft.typ === "pfad" && draft.punkte.length < 3)) {
        setAnnos((prev) => [...prev, { id: neuId(), seite, breite: draft.typ === "pfad" ? 2 : 1.5, ...draft }]);
      }
      setDraft(null);
    }
  };
  // griff=true: der Verschiebe-Griff (⠿) einer Textbox/eines Stempels wirkt in jedem Werkzeug (Welle 135 –
  // nach dem Setzen ist noch das Stempel-Werkzeug aktiv, sonst ließ sich der Stempel nicht verschieben)
  const annoMoveStart = (e, a, griff = false) => {
    if (tool !== "auswahl" && !griff) return;
    e.stopPropagation();
    setAktiveId(a.id);
    dragRef.current = { modus: "move", id: a.id, start: toPdf(e), orig: { x: a.x, y: a.y, punkte: a.punkte } };
    layerRef.current.setPointerCapture(e.pointerId);
  };
  const annoLoeschen = (id) => { setAnnos((prev) => prev.filter((a) => a.id !== id)); setAktiveId(null); };

  const svgEl = (a, key, vorschau = false) => {
    const stroke = a.farbe || "#000";
    const w = (a.breite || 1.5) * scale;
    const gemeinsam = {
      key, style: { pointerEvents: tool === "auswahl" && !vorschau ? "auto" : "none", cursor: "move" },
      onPointerDown: vorschau ? undefined : (e) => annoMoveStart(e, a),
    };
    const sel = !vorschau && a.id === aktiveId;
    if (a.typ === "pfad") {
      const pts = a.punkte.map(([x, y]) => toCss(x, y).join(",")).join(" ");
      return <polyline {...gemeinsam} points={pts} fill="none" stroke={stroke} strokeWidth={w}
        strokeLinecap="round" strokeLinejoin="round" opacity={sel ? 0.7 : 1} />;
    }
    if (a.typ === "linie") {
      const [x1, y1] = toCss(a.punkte[0][0], a.punkte[0][1]);
      const [x2, y2] = toCss(a.punkte[1][0], a.punkte[1][1]);
      return <line {...gemeinsam} x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth={w} opacity={sel ? 0.7 : 1} />;
    }
    // Rechteck-Familie
    const [ax, ay] = toCss(a.punkte[0][0], a.punkte[0][1]);
    const [bx, by] = toCss(a.punkte[1][0], a.punkte[1][1]);
    const x = Math.min(ax, bx), y = Math.min(ay, by), rw = Math.abs(bx - ax), rh = Math.abs(by - ay);
    if (a.typ === "marker") {
      return <rect {...gemeinsam} x={x} y={y} width={rw} height={rh} fill="#ffee00" opacity={0.35}
        stroke={sel ? "#d81b60" : "none"} strokeDasharray={sel ? "4 3" : undefined} />;
    }
    if (a.typ === "abdecken") {
      return <rect {...gemeinsam} x={x} y={y} width={rw} height={rh} fill="#ffffff"
        stroke={sel ? "#d81b60" : "#bbb"} strokeDasharray="4 3" strokeWidth={1} />;
    }
    return <rect {...gemeinsam} x={x} y={y} width={rw} height={rh} fill="none" stroke={stroke}
      strokeWidth={w} strokeDasharray={sel ? "4 3" : undefined} />;
  };

  const seitenAnnos = annos.filter((a) => a.seite === seite);
  const feldListe = Object.values(felder || {}).filter((f) => f.seite === seite);
  const cursorJeTool = tool === "auswahl" ? "default" : tool === "text" ? "text" : "crosshair";

  return (
    <div ref={layerRef} className="v-pdf-edit-layer" style={{ cursor: cursorJeTool }}
         onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}>
      <svg width={Math.floor(vp.width)} height={Math.floor(vp.height)} style={{ position: "absolute", inset: 0 }}>
        {seitenAnnos.filter((a) => a.typ !== "text" && !a.typ.startsWith("stempel")).map((a) => svgEl(a, a.id))}
        {seitenAnnos.filter((a) => a.typ.startsWith("stempel")).map((a) => {
          const [cx, cy] = toCss(a.x, a.y);
          const sel = a.id === aktiveId;
          return (
            <text key={a.id} x={cx} y={cy} fontSize={(a.groesse || 14) * scale} fill={a.farbe || "#000"}
              style={{ pointerEvents: tool === "auswahl" ? "auto" : "none", cursor: "move", fontWeight: 700, opacity: sel ? 0.7 : 1 }}
              onPointerDown={(e) => annoMoveStart(e, a)}>
              {a.typ === "stempel_haken" ? "✓" : "✕"}
            </text>
          );
        })}
        {draft && svgEl(draft, "draft", true)}
      </svg>

      {/* Text-Boxen */}
      {seitenAnnos.filter((a) => a.typ === "text").map((a) => {
        const [cx, cy] = toCss(a.x, a.y);
        const fs = (a.groesse || 12) * scale;
        const sel = a.id === aktiveId;
        return (
          <div key={a.id} className={"v-edit-textbox" + (sel ? " sel" : "") + (a.rahmen ? " rahmen" : "")}
               style={{ left: cx, top: cy - fs }}
               onPointerDown={(e) => e.stopPropagation()}>
            <div className="v-edit-grip" title="Verschieben"
                 onPointerDown={(e) => { e.preventDefault(); annoMoveStart(e, a, true); }}>⠿</div>
            <textarea
              ref={(el) => {
                if (el) textRefs.current[a.id] = el; else delete textRefs.current[a.id];
                // Fokus erst nach dem Einhängen setzen — autoFocus allein verliert gegen
                // die Fokus-Vergabe des laufenden Klicks.
                if (el && neuBoxRef.current === a.id) {
                  neuBoxRef.current = null;
                  requestAnimationFrame(() => { try { el.focus(); } catch {} });
                }
              }}
              value={a.text}
              style={{ fontSize: fs, lineHeight: 1.2, color: a.farbe || "#000",
                       ...(a.rahmen ? { borderColor: a.farbe || "#000" } : {}) }}
              rows={Math.max(1, a.text.split("\n").length)}
              cols={Math.max(8, ...a.text.split("\n").map((z) => z.length)) + (a.rahmen ? 3 : 1)}
              onFocus={() => setAktiveId(a.id)}
              onChange={(e) => setAnnos((prev) => prev.map((x) => x.id === a.id ? { ...x, text: e.target.value } : x))}
              onBlur={() => {
                // Verzögert und gegen den AKTUELLEN Stand prüfen: eine gerade erst angelegte
                // Box darf nicht verschwinden, nur weil der Fokus kurz wandert.
                setTimeout(() => {
                  if (document.activeElement === textRefs.current[a.id]) return;
                  setAnnos((prev) => {
                    const akt = prev.find((x) => x.id === a.id);
                    return akt && !(akt.text || "").trim() ? prev.filter((x) => x.id !== a.id) : prev;
                  });
                }, 150);
              }}
            />
            <button className="v-edit-del" title="Löschen"
                    onPointerDown={(e) => { e.stopPropagation(); annoLoeschen(a.id); }}>✕</button>
          </div>
        );
      })}

      {/* Lösch-Knopf für selektierte Zeichnung/Stempel */}
      {tool === "auswahl" && aktiveId != null && (() => {
        const a = seitenAnnos.find((x) => x.id === aktiveId && x.typ !== "text");
        if (!a) return null;
        const ref = a.punkte?.length ? a.punkte[0] : [a.x, a.y];
        const [cx, cy] = toCss(ref[0], ref[1]);
        return <button className="v-edit-del schwebend" style={{ left: cx - 10, top: cy - 26 }}
          title="Löschen" onPointerDown={(e) => { e.stopPropagation(); annoLoeschen(a.id); }}>✕ löschen</button>;
      })()}

      {/* Formularfelder */}
      {feldListe.map((f) => {
        const [x1, y1] = toCss(f.rect[0], f.rect[3]);
        const [x2, y2] = toCss(f.rect[2], f.rect[1]);
        const st = { left: x1, top: y1, width: Math.max(10, x2 - x1), height: Math.max(10, y2 - y1) };
        const setzen = (wert) => setFelder((prev) => ({ ...prev, [f.name]: { ...prev[f.name], wert, geaendert: true } }));
        if (f.typ === "CheckBox") {
          const an = !["", "Off", "false", "0", false].includes(f.wert);
          return (
            <label key={f.name} className="v-edit-feld check" style={st} title={f.name}
                   onPointerDown={(e) => e.stopPropagation()}>
              <input type="checkbox" checked={an} onChange={(e) => setzen(e.target.checked ? "1" : "")} />
            </label>
          );
        }
        return (
          <input key={f.name} className="v-edit-feld" style={{ ...st, fontSize: Math.max(9, st.height * 0.6) }}
                 title={f.name} value={f.wert || ""} list={f.optionen ? `opts-${f.name}` : undefined}
                 onPointerDown={(e) => e.stopPropagation()}
                 onChange={(e) => setzen(e.target.value)} />
        );
      })}
      {feldListe.filter((f) => f.optionen).map((f) => (
        <datalist key={`dl-${f.name}`} id={`opts-${f.name}`}>
          {f.optionen.map((o) => <option key={o} value={Array.isArray(o) ? o[0] : o} />)}
        </datalist>
      ))}
    </div>
  );
}
